import { BackendSmartAccount, BackendTransaction } from '~/types'
import { Address } from '~/utils/address'

export type EoaSigningReadinessFailureReason =
  'already-signed' | 'not-a-signer' | 'wallet-not-ready'

type EoaSigningReadiness =
  { ok: true } | { ok: false; reason: EoaSigningReadinessFailureReason }

/**
 * True when `connectedAddress` matches one of the smart account's EOA signer
 * addresses. Exposed as a standalone predicate because
 * `ApproveTransactionButton`/`useApproveEmailSigner` derive
 * `isEmailSignerWalletReady` from the same check and should not duplicate the
 * `Address.equal` loop.
 */
export const connectedAddressIsEoaSigner = ({
  connectedAddress,
  smartAccount,
}: {
  connectedAddress: string | undefined
  smartAccount: Pick<BackendSmartAccount, 'eoaSigners'>
}): boolean => {
  if (!connectedAddress) return false
  return smartAccount.eoaSigners.some((eoaSigner) =>
    Address.equal(eoaSigner.address, connectedAddress),
  )
}

/**
 * Pure validator for whether the connected EOA is ready to sign a transaction
 * on behalf of a smart account's email-only signer. Used by
 * `useApproveAndSubmitTransaction` to guard the EOA signing branch and surface
 * a specific error toast for each failure reason.
 *
 * Returns `{ ok: true }` when the connected wallet is an authorized signer
 * that has not yet signed, or `{ ok: false, reason }` with:
 *
 * - `wallet-not-ready`: no `connectedAddress` yet (wagmi still activating the
 *   Privy embedded wallet).
 * - `not-a-signer`: connected address is not among `smartAccount.eoaSigners`.
 * - `already-signed`: connected address has already signed this transaction.
 */
export const checkEoaSigningReadiness = ({
  connectedAddress,
  smartAccount,
  transaction,
}: {
  connectedAddress: string | undefined
  smartAccount: BackendSmartAccount
  transaction: BackendTransaction
}): EoaSigningReadiness => {
  if (!connectedAddress) {
    return { ok: false, reason: 'wallet-not-ready' }
  }

  const alreadySigned = transaction.eoaSigners.some((eoaSigner) =>
    Address.equal(eoaSigner.address, connectedAddress),
  )
  if (alreadySigned) {
    return { ok: false, reason: 'already-signed' }
  }

  if (!connectedAddressIsEoaSigner({ connectedAddress, smartAccount })) {
    return { ok: false, reason: 'not-a-signer' }
  }

  return { ok: true }
}
